/**
 * SupplierFullScanner — Iter 411 (Dirk, 2026-02-28).
 *
 * The equivalent of OcrInvoicesPage but for supplier documents.
 * URL: /ocr/supplier-invoices?supplier_id=X&doc=Y
 *
 * Purpose (Dirk): "OCR mistakes on Purchase Orders and GRVs must be
 * fixable BEFORE we push them to the Procurement Tracker. I need the
 * same scanner workspace we have on customer sales orders."
 *
 * What operators can do here:
 *   - See the OCR reconciliation banner (safety net — sum vs subtotal)
 *   - Edit per-line: SKU, description, qty, unit price
 *   - Match/re-match each line to an inventory item (autocomplete)
 *   - SPLIT a row into two half-rows
 *   - MERGE two adjacent rows
 *   - IGNORE a row (excluded from the reconciliation sum)
 *   - Save the corrected line_items back to the supplier document
 *   - Re-scan trigger (fresh OCR pass)
 *   - Deep-link into Procurement (Send-to-tracker) once satisfied.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Loader2, Save, RefreshCw, Trash2, AlertTriangle,
  CheckCircle2, AlertCircle, Split, Merge, Search, X, Plus,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";

function formatMoney(n) {
  const v = Number(n || 0);
  return `R ${v.toLocaleString("en-GB", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
}

export default function SupplierFullScanner() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const supplierId = params.get("supplier_id") || "";
  const docId = params.get("doc") || "";

  const [supplier, setSupplier] = useState(null);
  const [doc, setDoc] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rescanning, setRescanning] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Iter 425.o — Post-save sync summary: unknown SKUs, cost updates,
  // and unusual cost changes returned by the line-items PUT.
  const [sync, setSync] = useState(null);
  const [quickAddDraft, setQuickAddDraft] = useState(null); // {sku, description, unit_price, line_index}
  const [quickAddBusy, setQuickAddBusy] = useState(false);

  const load = useCallback(async () => {
    if (!supplierId || !docId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [sRes, dRes] = await Promise.all([
        api.get(`/suppliers/${supplierId}`),
        api.get(`/suppliers/${supplierId}/documents/${docId}`),
      ]);
      setSupplier(sRes.data);
      setDoc(dRes.data);
      setLines((dRes.data.line_items || []).map((ln, i) => ({
        ...ln, _rid: `r-${i}-${Math.random().toString(36).slice(2, 8)}`,
      })));
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed.");
    } finally {
      setLoading(false);
    }
  }, [supplierId, docId]);

  useEffect(() => { load(); }, [load]);

  const editLine = (rid, patch) => {
    setLines((prev) => prev.map((ln) => (ln._rid === rid ? { ...ln, ...patch } : ln)));
    setDirty(true);
  };
  const deleteLine = (rid) => {
    setLines((prev) => prev.filter((ln) => ln._rid !== rid));
    setDirty(true);
  };
  const insertLineAt = (idx) => {
    setLines((prev) => {
      const next = [...prev];
      next.splice(idx + 1, 0, {
        _rid: `r-new-${Math.random().toString(36).slice(2, 8)}`,
        sku: "", name: "", qty: 1, unit_price: 0, line_total: 0,
      });
      return next;
    });
    setDirty(true);
  };
  const splitLine = (rid) => {
    setLines((prev) => {
      const idx = prev.findIndex((ln) => ln._rid === rid);
      if (idx < 0) return prev;
      const src = prev[idx];
      const qty = Number(src.qty || 0);
      const half1 = Math.floor(qty / 2);
      const half2 = qty - half1;
      const price = Number(src.unit_price || 0);
      const clone = (q) => ({
        ...src,
        _rid: `r-split-${Math.random().toString(36).slice(2, 8)}`,
        qty: q,
        line_total: q * price,
      });
      const next = [...prev];
      next[idx] = { ...src, qty: half1, line_total: half1 * price };
      next.splice(idx + 1, 0, clone(half2));
      return next;
    });
    setDirty(true);
  };
  const mergeIntoPrev = (rid) => {
    setLines((prev) => {
      const idx = prev.findIndex((ln) => ln._rid === rid);
      if (idx <= 0) return prev;
      const src = prev[idx];
      const tgt = prev[idx - 1];
      const qty = Number(tgt.qty || 0) + Number(src.qty || 0);
      const price = Number(tgt.unit_price || 0);
      const next = [...prev];
      next[idx - 1] = {
        ...tgt,
        qty,
        line_total: qty * price,
      };
      next.splice(idx, 1);
      return next;
    });
    setDirty(true);
  };
  const toggleIgnore = (rid) => {
    setLines((prev) => prev.map((ln) => (
      ln._rid === rid
        ? { ...ln, action: (ln.action || "").toLowerCase() === "ignore" ? "" : "ignore" }
        : ln
    )));
    setDirty(true);
  };

  const totals = useMemo(() => {
    let computed = 0;
    let ignoredCount = 0;
    for (const ln of lines) {
      if ((ln.action || "").toLowerCase() === "ignore") { ignoredCount += 1; continue; }
      const lt = ln.line_total != null
        ? Number(ln.line_total)
        : Number(ln.qty || 0) * Number(ln.unit_price || 0);
      if (!Number.isNaN(lt)) computed += lt;
    }
    const extracted = doc?.totals?.subtotal;
    const extractedN = extracted != null ? Number(extracted) : null;
    const delta = extractedN != null ? computed - extractedN : null;
    const deltaPct = extractedN ? Math.abs(delta) / (Math.abs(extractedN) || 1) * 100 : null;
    return { computed, extracted: extractedN, delta, deltaPct, ignoredCount };
  }, [lines, doc]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        line_items: lines.map((ln) => {
          const { _rid, ...rest } = ln;
          return rest;
        }),
      };
      const { data } = await api.put(
        `/suppliers/${supplierId}/documents/${docId}/line-items`,
        payload,
      );
      setDoc(data);
      setLines((data.line_items || []).map((ln, i) => ({
        ...ln, _rid: `r-${i}-${Math.random().toString(36).slice(2, 8)}`,
      })));
      setDirty(false);
      // Iter 425.o — Show the sync summary card (unknown SKUs +
      // cost updates + unusual changes) returned alongside the save.
      setSync(data?._sync || null);
      const s = data?._sync || {};
      const bits = [];
      if ((s.unknown_skus || []).length) bits.push(`${s.unknown_skus.length} unknown SKU${s.unknown_skus.length === 1 ? "" : "s"}`);
      if ((s.cost_updates || []).length) bits.push(`${s.cost_updates.length} cost update${s.cost_updates.length === 1 ? "" : "s"}`);
      if ((s.unusual_cost_changes || []).length) bits.push(`${s.unusual_cost_changes.length} unusual cost change${s.unusual_cost_changes.length === 1 ? "" : "s"} flagged`);
      toast.success(`Lines saved${bits.length ? ` · ${bits.join(" · ")}` : ""}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const rescan = async () => {
    if (dirty && !window.confirm("Unsaved edits will be discarded when the fresh OCR pass completes. Continue?")) return;
    setRescanning(true);
    try {
      await api.post(`/suppliers/${supplierId}/documents/${docId}/rescan-ocr`);
      toast.success("Re-scan queued.");
      // Poll every 4s for up to 3 min.
      for (let i = 0; i < 45; i += 1) {
        await new Promise((r) => setTimeout(r, 4000));
        const { data } = await api.get(`/suppliers/${supplierId}/documents/${docId}`);
        if (data.ocr_status === "ready" || data.ocr_status === "error") {
          setDoc(data);
          setLines((data.line_items || []).map((ln, k) => ({
            ...ln, _rid: `r-${k}-${Math.random().toString(36).slice(2, 8)}`,
          })));
          setDirty(false);
          break;
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-scan failed.");
    } finally {
      setRescanning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#a0aabf]">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading scanner…
      </div>
    );
  }
  if (!supplierId || !docId || !doc) {
    return (
      <div className="min-h-screen p-6 text-[#a0aabf]" data-testid="supplier-scanner-empty">
        <div className="hv-card p-6 text-center">
          <div className="text-lg font-bold mb-2">Missing supplier or document</div>
          <div className="text-sm">This page needs both <code>?supplier_id=</code> and <code>?doc=</code>.</div>
          <button onClick={() => navigate(-1)} className="hv-btn hv-btn-primary mt-4">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>
      </div>
    );
  }

  const reconStatus = doc.ocr_reconciliation?.status;
  const showRedBanner = reconStatus === "mismatch";
  const showGreenBanner = reconStatus === "match";

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="supplier-full-scanner">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => navigate(`/suppliers/${supplierId}`)}
          className="hv-btn hv-btn-ghost text-sm"
          data-testid="supplier-scanner-back"
        >
          <ArrowLeft className="w-4 h-4" /> Supplier
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-[#647087]">Full scanner</div>
          <div className="text-xl font-bold truncate">
            {doc.name || doc.document_number || "Document"}
          </div>
          <div className="text-[11px] text-[#a0aabf]">
            {supplier?.name || ""}
            {doc.document_number ? ` · ${doc.document_number}` : ""}
            {doc.document_date ? ` · ${doc.document_date}` : ""}
            {doc.doc_type ? ` · ${doc.doc_type}` : ""}
          </div>
        </div>
        <button
          onClick={rescan}
          disabled={rescanning}
          className="hv-btn hv-btn-ghost text-sm"
          data-testid="supplier-scanner-rescan"
        >
          {rescanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Re-scan OCR
        </button>
        <button
          onClick={save}
          disabled={saving || !dirty}
          className="hv-btn hv-btn-primary text-sm"
          data-testid="supplier-scanner-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save
        </button>
      </div>

      {/* Reconciliation banner */}
      {showRedBanner && (
        <div className="hv-card p-4 border-rose-500/40 bg-rose-500/10"
             data-testid="supplier-scanner-recon-mismatch">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
            <div className="text-sm text-rose-200">
              <div className="font-bold">OCR review needed · Δ {formatMoney(Math.abs(doc.ocr_reconciliation?.delta || 0))}</div>
              <div className="text-[12px] text-rose-200/80 mt-1">
                {doc.ocr_reconciliation?.note}
              </div>
              <div className="text-[11px] text-rose-200/60 mt-1 font-mono">
                Lines sum {formatMoney(doc.ocr_reconciliation?.computed_lines_total || 0)} · Extracted subtotal {formatMoney(doc.ocr_reconciliation?.extracted_subtotal || 0)}
              </div>
            </div>
          </div>
        </div>
      )}
      {showGreenBanner && (
        <div className="hv-card p-3 border-emerald-500/40 bg-emerald-500/10"
             data-testid="supplier-scanner-recon-match">
          <div className="flex items-center gap-2 text-emerald-200 text-sm">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            <div>
              <span className="font-bold">Reconciled</span> ·
              Lines sum {formatMoney(doc.ocr_reconciliation?.computed_lines_total || 0)} matches extracted subtotal
              ({doc.ocr_reconciliation?.line_count} lines).
            </div>
          </div>
        </div>
      )}

      {/* Live totals strip (recomputed as user edits) */}
      <div className="hv-card p-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div>
          <div className="text-[10px] uppercase text-[#647087]">Lines</div>
          <div className="font-mono text-lg font-bold">{lines.length}</div>
          {totals.ignoredCount > 0 && (
            <div className="text-[10px] text-[#647087]">{totals.ignoredCount} ignored</div>
          )}
        </div>
        <div>
          <div className="text-[10px] uppercase text-[#647087]">Computed sum</div>
          <div className="font-mono text-lg font-bold">{formatMoney(totals.computed)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-[#647087]">Extracted subtotal</div>
          <div className="font-mono text-lg font-bold">
            {totals.extracted != null ? formatMoney(totals.extracted) : "—"}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-[#647087]">Δ (live)</div>
          <div className={`font-mono text-lg font-bold ${
            totals.delta == null ? "text-[#a0aabf]"
            : Math.abs(totals.delta) <= 5 || (totals.deltaPct ?? 100) <= 2 ? "text-emerald-300"
            : "text-rose-300"
          }`} data-testid="supplier-scanner-live-delta">
            {totals.delta == null ? "—" : formatMoney(totals.delta)}
          </div>
          {totals.deltaPct != null && (
            <div className="text-[10px] text-[#647087]">{totals.deltaPct.toFixed(1)}%</div>
          )}
        </div>
      </div>

      {/* Iter 425.o — Salesflow ⇄ Inventory sync review card. Shows
          unknown SKUs (per-row Add), cost updates that were applied,
          and unusual swings that were flagged (not applied). */}
      {sync && (sync.unknown_skus?.length || sync.cost_updates?.length || sync.unusual_cost_changes?.length) ? (
        <div className="hv-card p-3 border-cyan-500/30 bg-cyan-500/[0.03]" data-testid="supplier-sync-card">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.18em] text-cyan-300 font-bold">
              Inventory sync summary
            </div>
            <button onClick={() => setSync(null)} className="text-[#647087] hover:text-white text-[10px]">Dismiss</button>
          </div>
          {sync.unknown_skus?.length > 0 && (
            <div className="mt-2">
              <div className="text-[11px] text-cyan-200 mb-1">
                {sync.unknown_skus.length} unknown SKU{sync.unknown_skus.length === 1 ? "" : "s"} — add to inventory so they match next time
              </div>
              <div className="space-y-1">
                {sync.unknown_skus.map((u) => (
                  <div key={u.line_index} className="flex items-center justify-between text-[11px] px-2 py-1 rounded bg-white/[0.03]">
                    <div>
                      <span className="font-mono text-[10px] text-[#647087] mr-2">{u.sku}</span>
                      <span className="text-[#dde3ec]">{u.description}</span>
                      <span className="ml-2 text-[10px] text-[#647087]">{formatMoney(u.unit_price)}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setQuickAddDraft({
                        sku: u.sku,
                        description: u.description || "",
                        unit_price: u.unit_price || 0,
                        line_index: u.line_index,
                      })}
                      className="hv-btn hv-btn-primary text-[10px]"
                      data-testid={`supplier-quick-add-${u.line_index}`}
                    >
                      Add to inventory
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {sync.cost_updates?.length > 0 && (
            <div className="mt-2">
              <div className="text-[11px] text-emerald-300 mb-1">
                Updated cost on {sync.cost_updates.length} inventory item{sync.cost_updates.length === 1 ? "" : "s"}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-[10px]">
                {sync.cost_updates.map((c) => (
                  <div key={c.inventory_id} className="px-2 py-0.5 rounded bg-emerald-500/5">
                    <span className="font-mono mr-1">{c.sku}</span>
                    <span className="text-[#647087]">{formatMoney(c.old_avg_cost)}</span>
                    <span className="text-emerald-300 mx-1">→</span>
                    <span className="text-emerald-200">{formatMoney(c.new_avg_cost)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {sync.unusual_cost_changes?.length > 0 && (
            <div className="mt-2">
              <div className="text-[11px] text-amber-300 mb-1">
                {sync.unusual_cost_changes.length} unusual cost change{sync.unusual_cost_changes.length === 1 ? "" : "s"} flagged — verify before applying (3x swing)
              </div>
              <div className="space-y-1 text-[10px]">
                {sync.unusual_cost_changes.map((c) => (
                  <div key={c.inventory_id} className="px-2 py-0.5 rounded bg-amber-500/5">
                    <span className="font-mono mr-1">{c.sku}</span>
                    current {formatMoney(c.old_avg_cost)} · invoice line {formatMoney(c.line_unit_price)}
                    (would move avg to {formatMoney(c.would_be_new_avg_cost)})
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* Iter 425.o — Quick-add inventory modal. */}
      {quickAddDraft && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          onClick={() => !quickAddBusy && setQuickAddDraft(null)}
          data-testid="supplier-quick-add-modal"
        >
          <div className="hv-card p-4 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="text-sm font-semibold text-white mb-2">Add to inventory</div>
            <div className="space-y-2">
              <div>
                <label className="text-[10px] text-[#647087] block mb-0.5">SKU (locked)</label>
                <input type="text" value={quickAddDraft.sku} readOnly className="hv-input text-[11px] w-full font-mono bg-black/20" />
              </div>
              <div>
                <label className="text-[10px] text-[#647087] block mb-0.5">Description</label>
                <input
                  type="text"
                  value={quickAddDraft.description}
                  onChange={(e) => setQuickAddDraft((d) => ({ ...d, description: e.target.value }))}
                  className="hv-input text-[11px] w-full"
                  autoFocus
                  data-testid="supplier-quick-add-description"
                />
              </div>
              <div>
                <label className="text-[10px] text-[#647087] block mb-0.5">Avg cost (prefilled from invoice)</label>
                <input
                  type="number" step="0.01" min="0"
                  value={quickAddDraft.unit_price}
                  onChange={(e) => setQuickAddDraft((d) => ({ ...d, unit_price: parseFloat(e.target.value) || 0 }))}
                  className="hv-input text-[11px] w-full"
                  data-testid="supplier-quick-add-cost"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-3">
              <button
                onClick={() => setQuickAddDraft(null)}
                className="hv-btn hv-btn-ghost text-[11px]"
                disabled={quickAddBusy}
              >Cancel</button>
              <button
                type="button"
                onClick={async () => {
                  if (!quickAddDraft.description.trim()) {
                    toast.error("Description required.");
                    return;
                  }
                  setQuickAddBusy(true);
                  try {
                    await api.post("/inventory/quick-add", {
                      sku: quickAddDraft.sku,
                      description: quickAddDraft.description,
                      avg_cost: quickAddDraft.unit_price || 0,
                      source_doc_id: docId,
                      source_line_index: quickAddDraft.line_index,
                    });
                    toast.success(`${quickAddDraft.sku} added to inventory.`);
                    // Remove the row from the unknown list — future saves auto-match.
                    setSync((s) => ({
                      ...(s || {}),
                      unknown_skus: (s?.unknown_skus || []).filter((u) => u.line_index !== quickAddDraft.line_index),
                    }));
                    setQuickAddDraft(null);
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Add failed.");
                  } finally {
                    setQuickAddBusy(false);
                  }
                }}
                disabled={quickAddBusy}
                className="hv-btn hv-btn-primary text-[11px]"
                data-testid="supplier-quick-add-save"
              >
                {quickAddBusy ? "Adding…" : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Line items table — desktop; card list on mobile (< md). */}
      <div className="hv-card p-0 overflow-hidden">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wider text-[#647087] border-b border-white/5">
              <tr>
                <th className="text-left py-2 px-2 w-8">#</th>
                <th className="text-left py-2 px-2 min-w-[6rem]">SKU</th>
                <th className="text-left py-2 px-2 min-w-[10rem]">Description</th>
                <th className="text-right py-2 px-2 w-20">Qty</th>
                <th className="text-right py-2 px-2 w-24">Unit price</th>
                <th className="text-right py-2 px-2 w-28">Line total</th>
                <th className="text-left py-2 px-2 w-40">Inventory match</th>
                <th className="text-right py-2 px-2 w-28"></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((ln, idx) => (
                <LineRow
                  key={ln._rid}
                  idx={idx}
                  ln={ln}
                  onEdit={(patch) => editLine(ln._rid, patch)}
                  onDelete={() => deleteLine(ln._rid)}
                  onSplit={() => splitLine(ln._rid)}
                  onMerge={idx > 0 ? () => mergeIntoPrev(ln._rid) : null}
                  onIgnore={() => toggleIgnore(ln._rid)}
                  onInsertBelow={() => insertLineAt(idx)}
                />
              ))}
              {lines.length === 0 && (
                <tr><td colSpan={8} className="py-6 text-center text-[#647087]">
                  No line items extracted. Click <button className="underline" onClick={() => insertLineAt(-1)}>add a line</button> or Re-scan.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Iter 411 (Dirk, 2026-02-28) — Mobile line-item cards. On
            small screens the 8-column table forces character-by-
            character wraps in narrow columns; render a stacked card
            per line instead. */}
        <div className="md:hidden divide-y divide-white/5">
          {lines.map((ln, idx) => (
            <LineCardMobile
              key={ln._rid}
              idx={idx}
              ln={ln}
              onEdit={(patch) => editLine(ln._rid, patch)}
              onDelete={() => deleteLine(ln._rid)}
              onSplit={() => splitLine(ln._rid)}
              onMerge={idx > 0 ? () => mergeIntoPrev(ln._rid) : null}
              onIgnore={() => toggleIgnore(ln._rid)}
              onInsertBelow={() => insertLineAt(idx)}
            />
          ))}
          {lines.length === 0 && (
            <div className="py-6 text-center text-[#647087] text-xs">
              No line items extracted. Click <button className="underline" onClick={() => insertLineAt(-1)}>add a line</button> or Re-scan.
            </div>
          )}
        </div>
        <div className="p-2 border-t border-white/5 flex items-center justify-between text-[11px]">
          <button
            onClick={() => insertLineAt(lines.length - 1)}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="supplier-scanner-add-line"
          >
            <Plus className="w-3 h-3" /> Add line
          </button>
          {dirty && (
            <span className="text-amber-300 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> Unsaved changes
            </span>
          )}
        </div>
      </div>

      {/* Deep links */}
      <div className="flex items-center gap-2 flex-wrap">
        <Link
          to={`/suppliers/${supplierId}`}
          className="hv-btn hv-btn-ghost text-xs"
          data-testid="supplier-scanner-back-supplier"
        >
          Back to supplier
        </Link>
      </div>
    </div>
  );
}


function LineRow({ idx, ln, onEdit, onDelete, onSplit, onMerge, onIgnore, onInsertBelow }) {
  const isIgnored = (ln.action || "").toLowerCase() === "ignore";
  return (
    <tr className={`border-b border-white/5 last:border-b-0 hover:bg-white/[0.02] ${isIgnored ? "opacity-40 line-through" : ""}`}
        data-testid={`supplier-scanner-line-${idx}`}>
      <td className="py-1.5 px-2 text-[#647087] font-mono">{idx + 1}</td>
      <td className="py-1.5 px-2">
        <input
          value={ln.sku || ""}
          onChange={(e) => onEdit({ sku: e.target.value })}
          className="hv-input text-xs py-0.5 w-full"
          placeholder="SKU"
          data-testid={`supplier-scanner-sku-${idx}`}
        />
      </td>
      <td className="py-1.5 px-2">
        <input
          value={ln.name || ln.description || ""}
          onChange={(e) => onEdit({ name: e.target.value })}
          className="hv-input text-xs py-0.5 w-full"
          placeholder="Description"
          data-testid={`supplier-scanner-name-${idx}`}
        />
      </td>
      <td className="py-1.5 px-2 text-right">
        <input
          type="number"
          step="any"
          value={ln.qty ?? ln.quantity ?? 0}
          onChange={(e) => {
            const q = Number(e.target.value || 0);
            onEdit({ qty: q, line_total: q * Number(ln.unit_price || 0) });
          }}
          className="hv-input text-xs py-0.5 w-full text-right"
          data-testid={`supplier-scanner-qty-${idx}`}
        />
      </td>
      <td className="py-1.5 px-2 text-right">
        <input
          type="number"
          step="any"
          value={ln.unit_price ?? 0}
          onChange={(e) => {
            const p = Number(e.target.value || 0);
            onEdit({ unit_price: p, line_total: Number(ln.qty || 0) * p });
          }}
          className="hv-input text-xs py-0.5 w-full text-right"
          data-testid={`supplier-scanner-price-${idx}`}
        />
      </td>
      <td className="py-1.5 px-2 text-right">
        <input
          type="number"
          step="any"
          value={ln.line_total ?? (Number(ln.qty || 0) * Number(ln.unit_price || 0))}
          onChange={(e) => onEdit({ line_total: Number(e.target.value || 0) })}
          className="hv-input text-xs py-0.5 w-full text-right font-mono"
          data-testid={`supplier-scanner-total-${idx}`}
        />
      </td>
      <td className="py-1.5 px-2">
        <InventoryMatcher ln={ln} onEdit={onEdit} idx={idx} />
      </td>
      <td className="py-1.5 px-2 text-right">
        <div className="flex items-center gap-1 justify-end">
          <button onClick={onSplit} className="text-[#a0aabf] hover:text-[#1FB6C1]"
                  title="Split row into two halves"
                  data-testid={`supplier-scanner-split-${idx}`}>
            <Split className="w-3.5 h-3.5" />
          </button>
          {onMerge && (
            <button onClick={onMerge} className="text-[#a0aabf] hover:text-[#1FB6C1]"
                    title="Merge into row above (qty summed)"
                    data-testid={`supplier-scanner-merge-${idx}`}>
              <Merge className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={onIgnore}
                  className={`text-[10px] uppercase font-bold px-1 py-0.5 rounded border ${isIgnored ? "border-emerald-400/40 text-emerald-300" : "border-white/10 text-[#a0aabf]"}`}
                  title={isIgnored ? "Unignore — include in totals" : "Ignore — exclude from totals"}
                  data-testid={`supplier-scanner-ignore-${idx}`}>
            {isIgnored ? "restore" : "ignore"}
          </button>
          <button onClick={onInsertBelow}
                  className="text-[#a0aabf] hover:text-emerald-300"
                  title="Insert row below"
                  data-testid={`supplier-scanner-insert-${idx}`}>
            <Plus className="w-3.5 h-3.5" />
          </button>
          <button onClick={onDelete} className="text-rose-300/60 hover:text-rose-300"
                  title="Delete row"
                  data-testid={`supplier-scanner-delete-${idx}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}


function LineCardMobile({ idx, ln, onEdit, onDelete, onSplit, onMerge, onIgnore, onInsertBelow }) {
  const isIgnored = (ln.action || "").toLowerCase() === "ignore";
  const lineTotal = ln.line_total ?? (Number(ln.qty || 0) * Number(ln.unit_price || 0));
  return (
    <div className={`p-3 space-y-2 ${isIgnored ? "opacity-40" : ""}`}
         data-testid={`supplier-scanner-mobile-line-${idx}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-mono text-[#647087]">#{idx + 1}</span>
        <InventoryMatcher ln={ln} onEdit={onEdit} idx={idx} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold flex flex-col gap-1 col-span-2">
          Description
          <input
            value={ln.name || ln.description || ""}
            onChange={(e) => onEdit({ name: e.target.value })}
            className="hv-input text-xs py-1"
            placeholder="Description"
            data-testid={`supplier-scanner-mob-name-${idx}`}
          />
        </label>
        <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold flex flex-col gap-1">
          SKU
          <input
            value={ln.sku || ""}
            onChange={(e) => onEdit({ sku: e.target.value })}
            className="hv-input text-xs py-1"
            placeholder="SKU"
            data-testid={`supplier-scanner-mob-sku-${idx}`}
          />
        </label>
        <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold flex flex-col gap-1">
          Qty
          <input
            type="number"
            step="any"
            value={ln.qty ?? ln.quantity ?? 0}
            onChange={(e) => {
              const q = Number(e.target.value || 0);
              onEdit({ qty: q, line_total: q * Number(ln.unit_price || 0) });
            }}
            className="hv-input text-xs py-1 text-right"
            data-testid={`supplier-scanner-mob-qty-${idx}`}
          />
        </label>
        <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold flex flex-col gap-1">
          Unit price
          <input
            type="number"
            step="any"
            value={ln.unit_price ?? 0}
            onChange={(e) => {
              const p = Number(e.target.value || 0);
              onEdit({ unit_price: p, line_total: Number(ln.qty || 0) * p });
            }}
            className="hv-input text-xs py-1 text-right"
            data-testid={`supplier-scanner-mob-price-${idx}`}
          />
        </label>
        <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold flex flex-col gap-1">
          Line total
          <input
            type="number"
            step="any"
            value={lineTotal}
            onChange={(e) => onEdit({ line_total: Number(e.target.value || 0) })}
            className="hv-input text-xs py-1 text-right font-mono"
            data-testid={`supplier-scanner-mob-total-${idx}`}
          />
        </label>
      </div>
      <div className="flex items-center gap-1 flex-wrap pt-1">
        <button onClick={onSplit} className="hv-btn hv-btn-ghost text-[10px] py-1 px-2"
                data-testid={`supplier-scanner-mob-split-${idx}`}>
          <Split className="w-3 h-3" /> Split
        </button>
        {onMerge && (
          <button onClick={onMerge} className="hv-btn hv-btn-ghost text-[10px] py-1 px-2"
                  data-testid={`supplier-scanner-mob-merge-${idx}`}>
            <Merge className="w-3 h-3" /> Merge up
          </button>
        )}
        <button onClick={onIgnore}
                className={`text-[10px] uppercase font-bold px-2 py-1 rounded border ${isIgnored ? "border-emerald-400/40 text-emerald-300" : "border-white/10 text-[#a0aabf]"}`}
                data-testid={`supplier-scanner-mob-ignore-${idx}`}>
          {isIgnored ? "Restore" : "Ignore"}
        </button>
        <button onClick={onInsertBelow} className="hv-btn hv-btn-ghost text-[10px] py-1 px-2"
                data-testid={`supplier-scanner-mob-insert-${idx}`}>
          <Plus className="w-3 h-3" /> Below
        </button>
        <button onClick={onDelete} className="ml-auto text-rose-300/60 hover:text-rose-300 text-[10px] px-2"
                data-testid={`supplier-scanner-mob-delete-${idx}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}


function InventoryMatcher({ ln, onEdit, idx }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const search = async (text) => {
    if (!text || text.trim().length < 2) { setResults([]); return; }
    setBusy(true);
    try {
      const { data } = await api.get(`/inventory`, {
        params: { q: text.trim(), paginated: true, limit: 10 },
      });
      const items = Array.isArray(data) ? data : (data?.items || []);
      setResults(items);
    } catch {
      setResults([]);
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => search(q), 250);
    return () => clearTimeout(t);
  }, [q, open]);

  if (ln.matched_inventory_id) {
    return (
      <div className="flex items-center gap-1 flex-wrap">
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 inline-flex items-center gap-1"
              data-testid={`supplier-scanner-match-${idx}`}>
          <CheckCircle2 className="w-3 h-3" />
          {ln.matched_inventory_name || "matched"}
        </span>
        <button
          onClick={() => onEdit({ matched_inventory_id: null, matched_inventory_name: null })}
          className="text-[#647087] hover:text-rose-300"
          title="Unmatch"
          data-testid={`supplier-scanner-unmatch-${idx}`}
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => { setOpen(!open); if (!open) setQ(ln.sku || ln.name || ""); }}
        className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-200 border border-amber-400/40 inline-flex items-center gap-1"
        data-testid={`supplier-scanner-match-btn-${idx}`}
      >
        <AlertTriangle className="w-3 h-3" /> match
      </button>
      {open && (
        <div className="absolute z-50 mt-1 right-0 w-64 bg-[#0e1420] border border-white/10 rounded shadow-xl p-2">
          <div className="flex items-center gap-1 mb-2">
            <Search className="w-3 h-3 text-[#647087]" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search SKU / name…"
              className="hv-input text-xs py-1 flex-1"
              data-testid={`supplier-scanner-match-input-${idx}`}
            />
            <button onClick={() => setOpen(false)} className="text-[#647087]">
              <X className="w-3 h-3" />
            </button>
          </div>
          <div className="max-h-48 overflow-y-auto space-y-0.5">
            {busy && <div className="text-[10px] text-[#647087] p-1"><Loader2 className="w-3 h-3 inline animate-spin" /> searching…</div>}
            {!busy && results.length === 0 && q.trim().length >= 2 && (
              <div className="text-[10px] text-[#647087] p-1">No matches.</div>
            )}
            {results.map((inv) => (
              <button
                key={inv.id}
                onClick={() => {
                  onEdit({
                    matched_inventory_id: inv.id,
                    matched_inventory_name: inv.name,
                    sku: ln.sku || inv.sku,
                  });
                  setOpen(false);
                }}
                className="w-full text-left px-2 py-1 rounded hover:bg-white/5 text-xs"
                data-testid={`supplier-scanner-match-result-${idx}-${inv.id}`}
              >
                <div className="font-bold truncate">{inv.name}</div>
                <div className="text-[10px] text-[#647087]">{inv.sku || "—"}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
